'use client';

import type { ReactNode } from 'react';
import { FindingsRenderer, parseFindingData } from './FindingsRenderer';
import { FindingsSearchRenderer } from '../../ToolCallsRenderers/renderers/FindingsSearchRenderer';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import {
  parseFindingsListData,
  parseFindingsSearchData,
} from './FindingsRenderer';
import type { OutputRenderContext } from '../../tool-config-types';
import type { Finding } from './types';

const parseFindingsFromMarkdown = (content: string): Finding[] | null => {
  const itemPattern =
    /-\s+\*\*(.*?)\*\*\s*\n\s*ID:\s*([^\n]+)(?:\n\s*Severity:\s*([^|]+)\|\s*Status:\s*([^\n]+))?(?:\n\s*Category:\s*([^\n]+))?(?:\n\s*Summary:\s*([^\n]+))?(?:\n\s*CVSS:\s*([^\n]+))?/gi;
  const findings: Finding[] = [];

  for (const match of content.matchAll(itemPattern)) {
    findings.push({
      findingTitle: match[1]?.trim(),
      findingId: match[2]?.trim(),
      severity: match[3]?.trim(),
      content: match[6]?.trim(),
      category: match[5]?.trim(),
      cvssScore: match[7]?.trim(),
    });
  }

  return findings.length > 0 ? findings : null;
};

const parseFindingFromMarkdown = (content: string): Finding | null => {
  const idMatch = content.match(/\bID:\s*([a-f0-9]{24})/i);
  const titleMatch = content.match(/## Finding Details\s*\n\*\*(.*?)\*\*/i);
  const severityMatch = content.match(/\bSeverity:\s*([^|\n]+)/i);
  const categoryMatch = content.match(/\bCategory:\s*([^\n]+)/i);
  const summaryMatch = content.match(/\bSummary:\s*([^\n]+)/i);

  if (!idMatch && !titleMatch) {
    return null;
  }

  return {
    findingId: idMatch?.[1],
    findingTitle: titleMatch?.[1]?.trim(),
    severity: severityMatch?.[1]?.trim(),
    category: categoryMatch?.[1]?.trim(),
    content: summaryMatch?.[1]?.trim(),
  };
};

export const renderFindingsListResult = (
  ctx: OutputRenderContext,
): ReactNode => {
  const { content, maxHeight, onOpenObject } = ctx;

  if (typeof content === 'object' && content !== null) {
    const findingsSearch = parseFindingsSearchData(content);
    if (findingsSearch) {
      return (
        <FindingsSearchRenderer
          findings={findingsSearch.findings}
          query={findingsSearch.query}
          totalResults={findingsSearch.totalResults}
          scoreThreshold={findingsSearch.scoreThreshold}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }

    const findings = parseFindingsListData(content);
    if (findings && findings.length > 0) {
      return (
        <FindingsRenderer
          findings={findings}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }
  } else if (typeof content === 'string') {
    const markdownFindings = parseFindingsFromMarkdown(content);
    if (markdownFindings) {
      return (
        <FindingsRenderer
          findings={markdownFindings}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }

    try {
      const parsed = JSON.parse(content);
      const findingsSearch = parseFindingsSearchData(parsed);
      if (findingsSearch) {
        return (
          <FindingsSearchRenderer
            findings={findingsSearch.findings}
            query={findingsSearch.query}
            totalResults={findingsSearch.totalResults}
            scoreThreshold={findingsSearch.scoreThreshold}
            maxHeight={maxHeight}
            onOpenObject={onOpenObject}
          />
        );
      }

      const findings = parseFindingsListData(parsed);
      if (findings && findings.length > 0) {
        return (
          <FindingsRenderer
            findings={findings}
            maxHeight={maxHeight}
            onOpenObject={onOpenObject}
          />
        );
      }
    } catch {
      // Fall through to JSON renderer
    }
  }

  return <JSONRenderer data={content} maxHeight={maxHeight} />;
};

export const renderFindingResult = (ctx: OutputRenderContext): ReactNode => {
  const { content, maxHeight, onOpenObject } = ctx;

  if (typeof content === 'string') {
    const finding = parseFindingFromMarkdown(content);
    if (finding) {
      return (
        <FindingsRenderer
          findings={[finding]}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }
  }

  if (typeof content === 'object' && content !== null) {
    const finding = parseFindingData(content);
    if (finding) {
      return (
        <FindingsRenderer
          findings={[finding]}
          maxHeight={maxHeight}
          onOpenObject={onOpenObject}
        />
      );
    }
  }

  return <JSONRenderer data={content} maxHeight={maxHeight} />;
};
